import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import API from '../lib/api';

const AuditContext = createContext(null);

export function AuditProvider({ children }) {
  const runsRequestRef = useRef(null);
  const [auditRun, setAuditRun] = useState(null);
  const [auditRuns, setAuditRuns] = useState([]);
  const [currentPhase, setCurrentPhase] = useState(1);
  const [chatOpen, setChatOpen] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem('zentis-theme') || 'dark');
  const [loading, setLoading] = useState(false);
  
  // Data stores for each phase - allows chat to trigger refreshes
  const [documents, setDocuments] = useState([]);
  const [controls, setControls] = useState([]);
  const [findings, setFindings] = useState([]);
  const [dataVersion, setDataVersion] = useState(0); // Increment to trigger re-renders

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.classList.toggle('light', theme !== 'dark');
    localStorage.setItem('zentis-theme', theme);
  }, [theme]);

  const resetRunData = useCallback(() => {
    setDocuments([]);
    setControls([]);
    setFindings([]);
    setDataVersion(0);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const hydrateRunWithReport = useCallback(async (run) => {
    if (!run?.id) return run;
    try {
      const { data: report } = await API.get(`/audit-runs/${run.id}/report`);
      if (report?.sections) {
        return { ...run, report };
      }
    } catch (e) {
      console.error('Failed to fetch report for run:', e);
    }
    return run;
  }, []);

  const fetchRuns = useCallback(async () => {
    if (runsRequestRef.current) {
      return runsRequestRef.current;
    }

    const request = API.get('/audit-runs')
      .then(({ data }) => {
        const runs = Array.isArray(data) ? data : [];
        setAuditRuns(runs);
        return runs;
      })
      .catch((e) => {
        console.error('Failed to fetch runs:', e);
        return [];
      })
      .finally(() => {
        runsRequestRef.current = null;
      });

    runsRequestRef.current = request;

    try {
      return await request;
    } catch (e) {
      return [];
    }
  }, []);

  const createRun = useCallback(async (name) => {
    const { data } = await API.post('/audit-runs', { name });
    resetRunData();
    setAuditRun(data);
    setCurrentPhase(1);
    await fetchRuns();
    return data;
  }, [fetchRuns, resetRunData]);

  const loadRun = useCallback(async (id) => {
    const { data } = await API.get(`/audit-runs/${id}`);
    const hydrated = await hydrateRunWithReport(data);
    resetRunData();
    setAuditRun(hydrated);
    setCurrentPhase(hydrated.current_phase);
    return hydrated;
  }, [hydrateRunWithReport, resetRunData]);

  const advancePhase = useCallback(async (nextPhase) => {
    if (!auditRun) return;
    setCurrentPhase(nextPhase);
    setAuditRun(prev => ({
      ...prev,
      current_phase: nextPhase,
      phases_completed: [...new Set([...(prev.phases_completed || []), nextPhase - 1])]
    }));
    try {
      await API.put(`/audit-runs/${auditRun.id}/phase`, { phase: nextPhase });
    } catch (e) {
      console.error('Failed to persist phase update:', e);
    }
  }, [auditRun]);

  const refreshRun = useCallback(async () => {
    if (!auditRun) return;
    const { data } = await API.get(`/audit-runs/${auditRun.id}`);
    const hydrated = await hydrateRunWithReport(data);
    setAuditRun(hydrated);
    setCurrentPhase(hydrated.current_phase);
    return hydrated;
  }, [auditRun, hydrateRunWithReport]);

  // Fetch documents for current audit run
  const fetchDocuments = useCallback(async () => {
    if (!auditRun) return [];
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/documents`);
      setDocuments(data);
      return data;
    } catch (e) {
      console.error('Failed to fetch documents:', e);
      return [];
    }
  }, [auditRun]);

  // Fetch controls for current audit run
  const fetchControls = useCallback(async () => {
    if (!auditRun) return [];
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/controls`);
      setControls(data);
      return data;
    } catch (e) {
      console.error('Failed to fetch controls:', e);
      return [];
    }
  }, [auditRun]);

  // Fetch findings for current audit run
  const fetchFindings = useCallback(async () => {
    if (!auditRun) return [];
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/findings`);
      setFindings(data);
      return data;
    } catch (e) {
      console.error('Failed to fetch findings:', e);
      return [];
    }
  }, [auditRun]);

  // Trigger a global data refresh (used by chat after actions)
  const triggerDataRefresh = useCallback((actionType) => {
    setDataVersion(v => v + 1);
    // Refresh specific data based on action type
    if (actionType === 'ingest' || actionType === 'documents') {
      fetchDocuments();
    } else if (actionType === 'extract_controls' || actionType === 'controls') {
      fetchControls();
    } else if (actionType === 'evaluate_evidence' || actionType === 'findings') {
      fetchFindings();
    } else if (actionType === 'all') {
      fetchDocuments();
      fetchControls();
      fetchFindings();
      refreshRun();
    }
  }, [fetchDocuments, fetchControls, fetchFindings, refreshRun]);

  return (
    <AuditContext.Provider value={{
      auditRun, setAuditRun, auditRuns, setAuditRuns,
      currentPhase, setCurrentPhase,
      chatOpen, setChatOpen,
      theme, toggleTheme,
      loading, setLoading,
      documents, controls, findings, dataVersion,
      createRun, loadRun, advancePhase, refreshRun, fetchRuns,
      fetchDocuments, fetchControls, fetchFindings, triggerDataRefresh
    }}>
      {children}
    </AuditContext.Provider>
  );
}

export const useAudit = () => useContext(AuditContext);
